import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { setPlaying, clearPlaying } from '../slices/play-movie.slice';
import { getSocket, requestCurrentState } from '../services/socket';
import * as playbackService from '../services/playback';

// The backend is pessimistic: POST /start answers 202 and orchestrates
// prepare -> load -> wait-loaded -> play server-side, emitting playback:step
// progress and only broadcasting playback:status 'playing' once the projector
// genuinely confirmed playback. The controls stay locked (playback:lock) for
// the whole sequence, so every state this hook renders is truthful.

// TMS spl_position lags the wall clock by ~1-3s; while steadily playing, trust
// the local 1s ticker over that jitter and only snap on real drift.
const POSITION_JITTER_S = 3;

// No-socket fallback for the 202 /start flow: poll until the backend reports
// the confirmed playing order. Matches the orchestration's worst case.
const START_FALLBACK_POLL_MS = 5000;
const START_FALLBACK_MAX_POLLS = 60;

const usePlayback = (orderId, movieId) => {
    const dispatch = useDispatch();
    const reduxPlaying = useSelector(state => state.playMovie.playing);
    // Org-wide lock driven by the Layout socket bridge (playback:lock events) —
    // true while ANY client's TMS control action is in flight.
    const isLocked = useSelector(state => !!state.playMovie.controlLock);
    // If Redux already says this order is the active playback, the backend has
    // committed it as playing — initialize the bar in Play state so it doesn't
    // render Stop for the brief moment before refreshStatus returns.
    const initialPlayStatus = reduxPlaying && reduxPlaying._id === orderId ? 'Play' : 'Stop';

    const [step, setStep] = useState(null);
    const [stepProgress, setStepProgress] = useState(null);
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [position, setPosition] = useState(0);
    const [duration, setDuration] = useState(0);
    const [playStatus, setPlayStatus] = useState(initialPlayStatus);
    const [isFinished, setIsFinished] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    // Seconds until the server auto-ejects this paused playback, or null when
    // no deadline is near. Only local/TMS sites ever push this.
    const [autoEjectIn, setAutoEjectIn] = useState(null);
    // No server position received yet — the ticker must not run during the
    // post-/play ramp only to snap back on the first real reading.
    const [hasSync, setHasSync] = useState(false);
    const isMounted = useRef(true);

    const playStatusRef = useRef(initialPlayStatus);
    useEffect(() => { playStatusRef.current = playStatus; }, [playStatus]);
    const playingOrderRef = useRef(reduxPlaying?._id ?? null);
    useEffect(() => { playingOrderRef.current = reduxPlaying?._id ?? null; }, [reduxPlaying]);

    // Socket event handlers
    useEffect(() => {
        const socket = getSocket();
        if (!socket) return;

        const handlePlaybackStep = (data) => {
            if (data.orderId === orderId) {
                setStep(data.step);
                setStepProgress(data.progress ?? null);
            }
        };

        const handlePlaybackStatus = (data) => {
            if (data.orderId === orderId) {
                if (data.status === 'playing') {
                    // The 202 /start flow resolves here: the projector confirmed.
                    setStep(null);
                    setStepProgress(null);
                    setIsLoading(false);
                    setPlayStatus('Play');
                } else if (data.status === 'paused') {
                    setPlayStatus('Pause');
                } else if (data.status === 'stopped') {
                    // Redux teardown (clearPlaying + order status) is owned by
                    // the Layout bridge, which knows the true post-show status.
                    setPlayStatus('Stop');
                } else if (data.status === 'finished') {
                    setIsFinished(true);
                }
            }
        };

        const handlePlaybackPosition = (data) => {
            if (data.orderId !== orderId || isDragging) return;
            if (data.duration) setDuration(data.duration);
            if (data.state_info) setPlayStatus(data.state_info);
            if (typeof data.position !== 'number') return;
            setHasSync(true);
            setPosition(prev => {
                const steadyPlay = data.state_info === 'Play' && playStatusRef.current === 'Play';
                return steadyPlay && Math.abs(data.position - prev) < POSITION_JITTER_S
                    ? prev
                    : data.position;
            });
        };

        // The server re-sends this every poll tick inside the warning window and
        // sends secondsRemaining: null the moment the pause ends, so the banner
        // is self-healing across reconnects.
        const handleAutoEjectWarning = (data) => {
            if (data.orderId !== orderId) return;
            setAutoEjectIn(typeof data.secondsRemaining === 'number' ? data.secondsRemaining : null);
        };

        const handlePlaybackError = (data) => {
            if (data.orderId === orderId) {
                setError(data.error);
                setStep(null);
                setStepProgress(null);
                setIsLoading(false);
            }
        };

        const handleCurrentState = (data) => {
            // The lock payload carries the /start orchestration's current step —
            // restore the spinner for components that mounted mid-orchestration
            // (SPA navigation or reconnect). Outside the orderId guard because a
            // lock-only snapshot has no top-level orderId.
            if (data.lock?.orderId === orderId && data.lock.step) {
                setStep(data.lock.step);
            }
            if (data.orderId === orderId) {
                setPosition(data.position);
                setDuration(data.duration);
                if (data.state_info) setPlayStatus(data.state_info);
                if (data.status === 'finished') setIsFinished(true);
                // The ramp-seeded server cache reports position 0 — only a live
                // snapshot (>0) should start the local ticker.
                if (typeof data.position === 'number' && data.position > 0) setHasSync(true);
            }
        };

        socket.on('playback:step', handlePlaybackStep);
        socket.on('playback:status', handlePlaybackStatus);
        socket.on('playback:position', handlePlaybackPosition);
        socket.on('playback:error', handlePlaybackError);
        socket.on('playback:current', handleCurrentState);
        socket.on('playback:auto-eject-warning', handleAutoEjectWarning);

        return () => {
            socket.off('playback:step', handlePlaybackStep);
            socket.off('playback:status', handlePlaybackStatus);
            socket.off('playback:position', handlePlaybackPosition);
            socket.off('playback:error', handlePlaybackError);
            socket.off('playback:current', handleCurrentState);
            socket.off('playback:auto-eject-warning', handleAutoEjectWarning);
        };
    }, [orderId, isDragging, dispatch]);

    // Simple 1-second increment when playing (freezes during isLoading for
    // actions; gated on hasSync so it never counts ahead of the projector
    // during the post-/play ramp).
    useEffect(() => {
        if (playStatus === 'Play' && hasSync && !isDragging && !isFinished && !isLoading) {
            const intervalId = setInterval(() => {
                setPosition(prev => prev + 1);
            }, 1000);
            return () => clearInterval(intervalId);
        }
    }, [playStatus, hasSync, isDragging, isFinished, isLoading]);

    // Tick the auto-eject countdown locally between the server's 2s pushes so
    // it reads as a real countdown; each push re-snaps it to the truth.
    const autoEjectPending = autoEjectIn !== null;
    useEffect(() => {
        if (!autoEjectPending) return undefined;
        const intervalId = setInterval(() => {
            setAutoEjectIn(prev => (prev === null ? null : Math.max(0, prev - 1)));
        }, 1000);
        return () => clearInterval(intervalId);
    }, [autoEjectPending]);

    // Ask the server for a fresh snapshot (state + lock + orchestration step)
    // once on mount, so a component mounting mid-action (SPA navigation during
    // a /start orchestration) starts truthful instead of waiting for the next
    // push. Deliberately not in the subscription effect — that one re-runs on
    // drag toggles and would re-request stale positions mid-seek.
    useEffect(() => {
        requestCurrentState();
    }, [orderId]);

    // Cleanup on unmount
    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    // No-socket fallback: without playback:status events the 202 flow has no
    // completion signal — poll until the backend reports the confirmed playing
    // order, then mount the bar. Dispatch is global, so it runs even unmounted.
    const pollForPlaying = useCallback(async () => {
        for (let i = 0; i < START_FALLBACK_MAX_POLLS; i++) {
            await new Promise(r => setTimeout(r, START_FALLBACK_POLL_MS));
            const result = await playbackService.getPlayingMovie();
            if (result.data) {
                dispatch(setPlaying(result.data));
                break;
            }
        }
        if (isMounted.current) {
            setStep(null);
            setIsLoading(false);
        }
    }, [dispatch]);

    const startPlayback = useCallback(async () => {
        setError(null);
        setIsLoading(true);
        setStep('preparing');

        const result = await playbackService.startMovie(movieId, orderId);
        if (result.error) {
            if (isMounted.current) {
                setError(result.error);
                setStep(null);
                setIsLoading(false);
            }
            return;
        }

        // 202 accepted — the backend orchestrates prepare/load/play and only
        // broadcasts 'playing' once the projector confirms. From here the
        // socket drives everything: steps via playback:step, completion via
        // playback:status (which mounts the bar through the Layout bridge),
        // failure via playback:error. A socket that exists but is down would
        // never deliver those, so it gets the same fallback as no socket.
        if (!getSocket()?.connected) {
            pollForPlaying();
        }
    }, [movieId, orderId, pollForPlaying]);

    const pause = useCallback(async () => {
        setIsLoading(true);
        const result = await playbackService.pauseMovie(movieId, orderId);
        if (isMounted.current) {
            setIsLoading(false);
            if (result.error) {
                setError(result.error);
            } else {
                setPlayStatus('Pause');
            }
        }
    }, [movieId, orderId]);

    const resume = useCallback(async () => {
        setIsLoading(true);
        const result = await playbackService.playMovie(movieId, orderId);
        if (isMounted.current) {
            setIsLoading(false);
            if (result.error) {
                setError(result.error);
            } else {
                setPlayStatus('Play');
                // The server's cancel arrives on the next poll tick; drop the
                // banner immediately so resuming feels decisive.
                setAutoEjectIn(null);
            }
        }
    }, [movieId, orderId]);

    const stop = useCallback(async () => {
        setIsLoading(true);
        const result = await playbackService.ejectMovie(movieId, orderId);

        // Multiple clients run the finished-countdown eject near-simultaneously —
        // if another client's eject won the org control lock, the winner's
        // 'stopped' broadcast clears this bar; nothing to surface here.
        if (result.error && result.code === 'CONTROL_IN_PROGRESS' && isFinished) {
            if (isMounted.current) setIsLoading(false);
            return;
        }

        // Auto-fallback ONLY when the TMS/projector itself is unreachable. A force here
        // marks the order finished-playback even though the projector may still be playing,
        // so we must not trigger it for validation/auth/internal (4xx/500) errors — only for
        // the TMS-connectivity classes (502/503).
        const isTmsUnreachable = !!result.error && (
            ['TMS_REQUEST_FAILED', 'TMS_AUTH_FAILED'].includes(result.code) ||
            [502, 503].includes(result.status)
        );

        if (isTmsUnreachable && isMounted.current) {
            const forced = await playbackService.ejectMovie(movieId, orderId, { force: true });
            if (!isMounted.current) return;
            setIsLoading(false);

            if (forced.error) {
                setError(result.error);
                return;
            }

            // Order status is updated by the Layout bridge from the server's
            // 'stopped' broadcast, which carries the true post-show status.
            dispatch(clearPlaying());
            setPlayStatus('Stop');
            setPosition(0);
            setIsFinished(false);
            toast.warning('TMS unreachable — force stopped. The projector may still need manual ejection.');
            return;
        }

        if (result.error && isMounted.current) {
            // Not a TMS-connectivity failure — surface the error, do NOT force a finished status.
            setIsLoading(false);
            setError(result.error);
            return;
        }

        if (isMounted.current) {
            setIsLoading(false);
            // Order status is updated by the Layout bridge from the server's
            // 'stopped' broadcast, which carries the true post-show status
            // (may be ready-to-playback, not finished-playback).
            dispatch(clearPlaying());
            setPlayStatus('Stop');
            setPosition(0);
            setIsFinished(false);
        }
    }, [movieId, orderId, isFinished, dispatch]);

    const seek = useCallback(async (timecode) => {
        setIsLoading(true);
        const result = await playbackService.goToTimecode(movieId, orderId, parseInt(timecode));
        if (isMounted.current) {
            setIsLoading(false);
            if (result.error) {
                setError(result.error);
            } else {
                setPosition(parseInt(timecode));
            }
        }
    }, [movieId, orderId]);

    const refreshStatus = useCallback(async () => {
        const result = await playbackService.getPlayStatus(movieId, orderId);
        if (result.data && isMounted.current) {
            const { spl_position, spl_duration, state_info } = result.data;
            setDuration(spl_duration ?? 0);
            // During the post-/play ramp the TMS reports Stop/0 while the backend
            // has committed the order as playing — don't regress the optimistic
            // Play bar; socket events are authoritative from here on.
            if (state_info === 'Stop' && !spl_position && playingOrderRef.current === orderId) return;
            setPosition(spl_position ?? 0);
            if (state_info) {
                setPlayStatus(state_info);
                if (state_info === 'Play') setHasSync(true);
            }
        }
    }, [movieId, orderId]);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    const setDragging = useCallback((dragging) => {
        setIsDragging(dragging);
    }, []);

    const setLocalPosition = useCallback((pos) => {
        setPosition(pos);
    }, []);

    return {
        // State
        step,
        stepProgress,
        error,
        isLoading,
        position,
        duration,
        playStatus,
        isFinished,
        isDragging,
        isLocked,
        autoEjectIn,

        // Actions
        startPlayback,
        pause,
        resume,
        stop,
        seek,
        refreshStatus,
        clearError,
        setDragging,
        setLocalPosition,
    };
};

export default usePlayback;
